import { Box, Stack } from "@chakra-ui/react";
import ComparisonTable from "@/components/comparisonTable";
import { ListWithLabel } from "@/components/list";
import { Text } from "@/elements";
import { TABLE_DATA } from "./tableConstants";

const DisplayList = ({
  text,
  points,
  concText,
}: {
  text: string;
  points: { label: string; text: string }[];
  concText?: string;
}) => {
  return (
    <Stack gap={3}>
      <Text variant={"body2"} color="text.100">
        {text}
      </Text>
      <ListWithLabel points={points} />
      {concText && (
        <Text variant={"body2"} color="text.100">
          {concText}
        </Text>
      )}
    </Stack>
  );
};

const TableComponent = () => {
  return (
    <Box>
      <ComparisonTable {...TABLE_DATA} />
      <Text variant={"body2"} color="text.100" mt={4}>
        OpenStack provides greater flexibility and control for businesses that require
        custom cloud environments, whereas AWS, Azure, and Google Cloud offer managed
        cloud services with less administrative overhead.
      </Text>
    </Box>
  );
};

export const FAQS = [
  {
    title: "What are the primary use cases for OpenStack Cloud?",
    content: (
      <DisplayList
        text={
          "OpenStack is versatile and can be used for various applications, including:"
        }
        points={[
          {
            label: "Private Cloud",
            text: "Enabling organizations to deploy cloud infrastructure on-premises for enhanced security and control.",
          },
          {
            label: "Public Cloud",
            text: "Allowing service providers to offer scalable cloud resources to customers.",
          },
          {
            label: "Hybrid Cloud",
            text: "Integrating private and public clouds to create a unified, flexible computing environment.",
          },
          {
            label: "Network Functions Virtualization (NFV)",
            text: "Supporting the virtualization of network services.",
          },
          {
            label: "Edge Computing",
            text: "Deploying resources closer to data sources for reduced latency.",
          },
        ]}
      />
    ),
  },
  {
    title: "How does OpenStack ensure security within the cloud environment?",
    content: (
      <DisplayList
        text={"OpenStack incorporates several security measures:"}
        points={[
          {
            label: "Keystone for Authentication",
            text: "Manages user authentication and enforces access controls.",
          },
          {
            label: "Role-Based Access Control (RBAC)",
            text: "Defines user roles and permissions to restrict access appropriately.",
          },
          {
            label: "Network Isolation",
            text: "Utilizes Neutron to create isolated networks for different tenants.",
          },
          {
            label: "Image Signing",
            text: "Ensures the integrity and authenticity of images in Glance.",
          },
        ]}
      />
    ),
  },
  {
    title: "Can OpenStack integrate with other cloud platforms? ",
    content:
      "Yes, OpenStack is designed with interoperability in mind. It can integrate with various cloud platforms and services, facilitating hybrid cloud deployments and enabling seamless workload migration between different environments. ",
  },
  {
    title: "What are the hardware requirements for deploying an OpenStack Cloud?",
    content: (
      <DisplayList
        text={
          "The hardware requirements for OpenStack can vary based on the scale and purpose of the deployment. Generally, a minimal setup might include:"
        }
        points={[
          {
            label: "Controller Node",
            text: "Manages OpenStack services and requires robust CPU and memory resources.",
          },
          {
            label: "Compute Nodes",
            text: "Run virtual machine instances and should have significant CPU and RAM capacities.",
          },
          {
            label: "Storage Nodes",
            text: "Provide block and object storage, necessitating ample disk space.",
          },
        ]}
        concText={
          "It's essential to assess specific workload requirements to determine the appropriate hardware specifications. "
        }
      />
    ),
  },
  {
    title: "How does OpenStack handle networking?",
    content: (
      <DisplayList
        text={
          "OpenStack uses the Neutron component to manage networking. Neutron provides Networking-as-a-Service (NaaS) between interface devices managed by other OpenStack services. It enables users to create and manage networks, subnets, and routers, offering flexibility in configuring network architectures. It supports advanced features like:"
        }
        points={[
          {
            label: "Software-Defined Networking (SDN)",
            text: "Enables dynamic and programmatic control of network resources.",
          },
          {
            label: "Load Balancing as a Service (LBaaS)",
            text: "Distributes traffic across multiple instances to optimize performance.",
          },
          {
            label: "Firewall as a Service (FWaaS)",
            text: "Implements security policies to protect cloud infrastructure.",
          },
          {
            label: "Virtual Private Networks (VPN)",
            text: "Provides secure connectivity between cloud resources and external networks.",
          },
        ]}
      />
    ),
  },
  {
    title:
      "What is the difference between OpenStack and other cloud platforms like AWS, Azure, and Google Cloud?",
    content: <TableComponent />,
  },
  {
    title: "Is OpenStack suitable for large-scale enterprises? ",
    content:
      "Yes, OpenStack is widely adopted by large enterprises and service providers for building scalable private and hybrid cloud environments. Companies that require custom cloud architectures, regulatory compliance, and cost-efficient cloud computing benefit from OpenStack’s open-source framework. ",
  },
  {
    title: "Can OpenStack be used for AI and Machine Learning workloads?",
    content: (
      <DisplayList
        text={
          "Yes! OpenStack supports AI/ML workloads through its high-performance computing (HPC) capabilities, including:"
        }
        points={[
          {
            label: "GPU Support",
            text: "OpenStack integrates with GPU acceleration technologies like NVIDIA CUDA for deep learning tasks.",
          },
          {
            label: "Containerized AI Workflows",
            text: "Kubernetes and OpenStack work together to provide containerized AI/ML model training environments.",
          },
          {
            label: "Big Data Processing",
            text: "OpenStack supports Hadoop and Spark for large-scale data analytics.",
          },
        ]}
      />
    ),
  },
  {
    title: "How does OpenStack enable hybrid cloud deployments?",
    content: (
      <DisplayList
        text={
          "OpenStack supports hybrid cloud environments by integrating with public cloud providers through multi-cloud orchestration tools like:"
        }
        points={[
          {
            label: "Terraform",
            text: "Automates OpenStack infrastructure alongside AWS, Azure, or Google Cloud.",
          },
          {
            label: "OpenStack Heat",
            text: "Provides orchestration for cross-cloud workload management.",
          },
          {
            label: "Kubernetes (K8s)",
            text: "Runs containerized applications across OpenStack and other cloud platforms.",
          },
        ]}
        concText={
          "This allows businesses to combine private cloud control with public cloud scalability, ensuring cost efficiency and flexibility. "
        }
      />
    ),
  },
  {
    title: "What are the challenges of deploying OpenStack?",
    content: (
      <DisplayList
        text={
          "While OpenStack offers many benefits, it also presents challenges such as:"
        }
        points={[
          {
            label: "Complex Deployment",
            text: "Requires expertise in networking, storage, and virtualization.",
          },
          {
            label: "Ongoing Maintenance",
            text: "Unlike managed cloud services, OpenStack requires continuous updates and security patches.",
          },
          {
            label: "Hardware Compatibility",
            text: "Not all hardware is OpenStack-compatible, requiring careful planning before deployment.",
          },
        ]}
        concText={
          "However, managed OpenStack services and pre-configured OpenStack distributions help reduce these complexities. "
        }
      />
    ),
  },
  {
    title: "How do I get started with OpenStack?",
    content: (
      <DisplayList
        text={"To get started with OpenStack, follow these steps:"}
        points={[
          {
            label: "Determine Your Use Case",
            text: "Decide if OpenStack will be used for private, hybrid, or public cloud deployment.",
          },
          {
            label: "Choose an OpenStack Distribution",
            text: "Select from vendors like Red Hat OpenStack, Mirantis OpenStack, or Canonical’s Ubuntu OpenStack.",
          },
          {
            label: "Set Up a Test Environment",
            text: "Deploy OpenStack using a cloud sandbox or virtualization tools like DevStack for testing.",
          },
          {
            label: "Implement a Scalable Architecture",
            text: "Configure compute, storage, and networking resources based on your workload requirements.",
          },
          {
            label: "Automate Management Tasks",
            text: "Use Ansible, Terraform, or Kubernetes to streamline infrastructure management.",
          },
          {
            label: "Engage with the OpenStack Community",
            text: "Leverage OpenStack forums, documentation, and online training resources for continuous learning.",
          },
        ]}
        concText={
          "For organizations seeking hassle-free OpenStack deployment, managed OpenStack service providers can assist in setup, scaling, and maintenance.   "
        }
      />
    ),
  },
];
